import type {
  KeyboardEvent as ReactKeyboardEvent,
  MutableRefObject,
} from "react";

import { ALL_COMMAND_DESCRIPTORS } from "@dxf-ai-editor/command-registry";

import type { DraftCommandId, DraftSession } from "../scene/draftingHelpers";
import { normalizeDraftCommand } from "../scene/draftingHelpers";
import { t } from "../i18n";
// A03 (R17 seam) — needed only by `createCommandLineInputKeyDownHandler`
// below, extracted verbatim from App.tsx's inline command-input `onKeyDown`.
// `pointFilterSession` mirrors the identical deep import
// `useKeyboardShortcuts.ts` already uses for the SAME point-filter concern
// on its global window listener. `useSysvarStore`, unlike that deep import,
// goes through the `../dynamicInput` barrel — `useCommandLine` is one of the
// barrel's own documented required consumers (see `dynamicInput/index.ts`'s
// header comment), and this mirrors how App.tsx imported the SAME hook for
// this SAME `onKeyDown` handler before the handler moved here (Codex PR #575
// review).
import { clearPointFilter, isPointFilterActive } from "../scene/pointFilterSession";
import { useSysvarStore } from "../dynamicInput";

export type CommandSuggestion = {
  id: string;
  label: string;
  insertText: string;
  submit?: boolean;
  kind: "command" | "keyword" | "example";
};

// D5 — two single-letter shell-keyboard shortcuts collide with a DIFFERENT
// command's legitimate, AutoCAD-documented registry alias:
//   • "T"  → real AutoCAD's "T" is MTEXT's alias (registry: annotation.ts);
//            this app's shell-keyboard layer has always sent bare "T" to
//            the single-line TEXT flow instead.
//   • "I"  → real AutoCAD's "I" is INSERT's alias (registry: block.ts);
//            this app's shell-keyboard layer has always collapsed bare "I"
//            onto the BLOCK flow (mirroring draftingHelpers.ts's
//            SPECIAL_CASE_OVERRIDES for the full word "INSERT"→BLOCK).
// Registering "T"/"I" directly on TEXT/BLOCK's registry aliases would
// collide with MTEXT/INSERT's real aliases (validator check C.ALIAS) and
// would misrepresent AutoCAD's actual convention to AI / docs consumers
// that read the registry. These two entries are therefore local,
// documented overrides applied AFTER the registry-derived pass — this is
// the ONLY app-specific behaviour NOT sourced from the registry, and it
// pre-dates D5 (preserved verbatim, not introduced by it).
const SHELL_KEYBOARD_ALIAS_LOCAL_OVERRIDES: Readonly<
  Record<string, DraftCommandId>
> = {
  T: "TEXT",
  I: "BLOCK",
};

// D5 round-5 (@codex review, PR #471) — the mirror-image problem to
// `NOT_YET_CLI_DISPATCHED_NON_DRAFT_IDS` further down this file: some
// `draftCommandId`-bearing descriptors ARE reachable through
// `startDraftSession` in principle, but only when the CALLER pre-arms
// `options` with context that no command-line/typed-text path can ever
// supply. GCCONSTRAINT is the concrete case — its own long help text in
// `command-registry/src/commands/system.ts` already documents this: "The
// command is not exposed on the standard command line ... command-line
// typed-name routing for GCCONSTRAINT is not wired yet". `startDraftSession`'s
// GCCONSTRAINT entry (draftingHelpers.ts) lands unconditionally on
// `geom-constraint-pick-first` — unlike MOVE/COPY/MIRROR/ROTATE/SCALE/
// ARRAYRECT/ARRAYPOLAR/ARRAYPATH/DDATTE/ATTEDIT/EATTEDIT/HATCHEDIT/
// MLEADEREDIT/BLOCK, which all have a graceful "no pre-armed options →
// land on the ordinary select-first (or name-prompt) step" fallback,
// GCCONSTRAINT has none: its very first click routes through
// `advanceGeomConstraintPick` (geomConstraintPicks.ts), which reads
// `session.options.geomKind` / `session.options.geomBlockName` and
// returns the session UNCHANGED whenever either is missing (`if (!kind ||
// !blockName) return { nextSession: session };`) — a session started
// without those options can NEVER advance; every click is silently
// swallowed. The only real entry point is `AppBlockAuthoringLayer`'s
// `onStartGeometricConstraint` (BlockAuthoringPalette), which always
// supplies `geomKind` + `geomBlockName` from the palette's own
// constraint-kind buttons inside an active BEDIT session — information
// typed command-line text cannot carry.
//
// Before this fix, `buildCommandSuggestionAliasPairs`'s `isDraft` check
// (`descriptor.draftCommandId !== undefined`) and
// `buildShellKeyboardCommandAliases` both included EVERY draft descriptor
// unconditionally, so typing "G" advertised "GCCONSTRAINT (GCCONSTRAINT)"
// in the suggestion dropdown, and selecting it (or typing the full alias
// + Enter, or even typing the letters on the canvas without Enter — the
// no-session `isExactShellKeyboardAlias` instant auto-submit path) called
// `submitCommandText`/`beginDrafting` with no options, producing the
// permanently-stuck session described above.
//
// Self-audit (round-5): every OTHER `draftCommandId` descriptor in
// `ALL_COMMAND_DESCRIPTORS` was checked against this same shape — "does
// `startDraftSession` land it somewhere useful when `options` is empty?"
// — and all of them do (see the fallback steps enumerated above). The
// `BPARAM-*` / `BACTION-*` draft ids in `draftTypes.ts` (Block Authoring
// parameter/action placement, also palette-only — `AppBlockAuthoringLayer`'s
// `onBeginPlace` primes them with `blockName` + existing-parameter/action
// metadata the same way GCCONSTRAINT is primed) are deliberately NOT in
// this set: they have no `command-registry` descriptor at all (no
// `draftCommandId: "BPARAM-..."` / `"BACTION-..."` entry anywhere under
// `packages/command-registry/src`), so they never reach
// `ALL_COMMAND_DESCRIPTORS` and `buildCommandSuggestionAliasPairs` /
// `buildShellKeyboardCommandAliases` / `normalizeDraftCommand` can never
// resolve them from typed text in the first place — the bug class this
// set guards against structurally cannot occur for them today.
// GCCONSTRAINT is therefore the sole current member; if a future
// descriptor gains a `draftCommandId` whose `startDraftSession` entry has
// no options-less fallback step, add it here too —
// `useCommandLine.paletteOnlyDraftCommands.test.ts` asserts the
// suggestion list omits every member and that a bare "GCCONSTRAINT" (or
// "ГЕОМОГРАН") full submission does not start a session at all.
export const PALETTE_ONLY_DRAFT_COMMAND_IDS: ReadonlySet<DraftCommandId> =
  new Set<DraftCommandId>(["GCCONSTRAINT"]);

// D5 (App.tsx router→registry unification) — this table used to be a
// hand-maintained literal duplicate of the aliases already declared on each
// command's `command-registry` descriptor (one of 3 divergent alias
// sources the D5 audit found: the registry itself, this table, and
// `buildCommandSuggestions`'s inline array below). It is now DERIVED from
// the registry at module load: every alias (id + EN + RU) of every
// descriptor that has a `draftCommandId` resolves through
// `normalizeDraftCommand` (which already owns the INSERT→BLOCK special
// case — see draftingHelpers.ts) exactly once, then the two local
// overrides above are applied. This is a superset of the previous
// hand-written table (same EN short forms, plus every RU alias the
// registry declares) — see `useCommandLine.shellAliasParity.test.ts` for
// the regression oracle proving no previously-working key/value pair was
// lost.
function buildShellKeyboardCommandAliases(): Record<string, DraftCommandId> {
  const table: Record<string, DraftCommandId> = {};
  for (const descriptor of ALL_COMMAND_DESCRIPTORS) {
    if (descriptor.draftCommandId === undefined) continue;
    // Palette-only draft commands (see `PALETTE_ONLY_DRAFT_COMMAND_IDS`'s
    // doc comment above) must never populate the auto-submit /
    // Enter-submission alias table — none of their aliases can safely
    // start a session from typed text alone.
    if (
      PALETTE_ONLY_DRAFT_COMMAND_IDS.has(
        descriptor.draftCommandId as DraftCommandId,
      )
    ) {
      continue;
    }
    const candidates = [
      descriptor.id,
      ...descriptor.aliases.en,
      ...descriptor.aliases.ru,
    ];
    for (const candidate of candidates) {
      if (table[candidate] !== undefined) continue;
      const resolved = normalizeDraftCommand(candidate);
      if (resolved !== null) table[candidate] = resolved;
    }
  }
  return { ...table, ...SHELL_KEYBOARD_ALIAS_LOCAL_OVERRIDES };
}

export const shellKeyboardCommandAliases: Record<string, DraftCommandId> =
  buildShellKeyboardCommandAliases();

// D5 follow-up (@codex review) — registry-deriving `shellKeyboardCommandAliases`
// exposed an ambiguous-prefix hazard the hand-written table used to dodge by
// omission: the no-Enter-needed canvas shell-keyboard path
// (`isExactShellKeyboardAlias`, consumed by useKeyboardShortcuts) fires the
// INSTANT the typed buffer matches ANY table key. Once the table is a
// superset containing every registry alias, a short key can collide with a
// longer, differently-resolving key sharing the same prefix — e.g. "M"
// resolves to MOVE, but "MT"/"ML"/"MLD"/"MV"/"MLE"/"MI" all start with "M"
// and resolve to *different* commands (MTEXT/MLEADER/MVIEW/MLEADEREDIT/
// MIRROR). Auto-submitting on bare "M" silently steals every keystroke
// aimed at those commands before the user finishes typing them. This is NOT
// unique to "M" — the same shape recurs across the whole registry (A/ARC vs
// AR*/ARRAY*/ATT*, B/BLOCK vs BE/BS/BC/BH, C/CIRCLE vs CO/CP/CHA, H/HATCH vs
// HE, L/LINE vs LEN/LWPOLYLINE, S/STRETCH vs SC/SPL, T/TEXT vs TR/T'KKBGC,
// DIM/DIMENSION vs DIM3/DIMANGULAR/DIMBASELINE/…, ML/MLEADER vs
// MLE/MLEADEREDIT, etc. — see `useCommandLine.autoSubmitAmbiguity.test.ts`).
//
// Fix: `shellKeyboardCommandAliases` itself stays the FULL, unpruned
// resolution table — Enter-key submission (`resolveShellDraftCommand`) and
// the no-session autocomplete list still need every alias, and an explicit
// Enter already disambiguates intent, so pruning the table itself would
// regress `useCommandLine.shellAliasParity.test.ts`'s superset guarantee.
// Instead, a SEPARATE guard set is precomputed once at module load: a key
// is "auto-submit ambiguous" iff some OTHER key in the SAME table is a
// strict prefix-extension of it that resolves to a DIFFERENT
// `DraftCommandId`. `isExactShellKeyboardAlias` below consults this set and
// withholds the instant auto-submit for ambiguous keys — the user still
// reaches the command by pressing Enter (unambiguous, full-table lookup) or
// by typing further until the buffer becomes unambiguous (e.g. "MOVE",
// "MLD", "MT", "ARC").
//
// D5 round-3 (@codex Finding 2) — the ambiguity universe below was scoped
// to ONLY `shellKeyboardCommandAliases` (draft-command aliases), because
// that used to be the sole reachable no-Enter target. But
// `submitCommandText`'s `resolvedCommandId` resolves the typed buffer
// against the FULL command registry (`resolveAlias(upperStart,
// ALL_COMMAND_DESCRIPTORS)` — see submitCommandText.ts), so a draft alias
// that's a strict PREFIX of a real, routed NON-draft command's alias is
// exactly as hazardous as two draft aliases colliding: instant auto-submit
// on the short draft alias steals every keystroke aimed at the longer
// non-draft command before the user finishes typing it. Concrete cases the
// draft-only universe missed entirely: typing "XREF" starts EXPLODE on the
// very first "X" (XREF/XATTACH/XBIND/XCLIP/XDETACH/XRELOAD/XUNLOAD/XOPEN
// all share the "X" prefix with EXPLODE's own "X" alias, plus
// EXPLODEINSERT's "XPLODE" alias — see the `NOT_YET_CLI_DISPATCHED_...`
// note further down for why EXPLODEINSERT itself still can't be typed to
// completion); typing "ATTSYNC" starts ATTDEF the instant the buffer hits
// "ATT"; typing "BCOUNT"/"BCPARAMETER" starts BCLOSE at "BC"; typing
// "COLOR"/"SETCOLOR" starts COPY at "CO"; typing "SCALELISTEDIT" starts
// SCALE at "SC"; typing "BLOCKPROPERTIESMANAGER" starts BLOCK the instant
// the buffer reaches the full word "BLOCK" itself (a non-draft command's
// alias can extend even a FULLY-TYPED draft command name, not just its
// short forms — see `useCommandLine.suggestions.notWiredGap.test.ts` /
// `useCommandLine.autoSubmitAmbiguity.test.ts` for the regression oracle).
//
// Fix: build an "alias identity universe" spanning EVERY descriptor in
// `ALL_COMMAND_DESCRIPTORS` (draft AND non-draft, any `status`) —
// `resolvedCommandId` can match any of them, so any of them can be "the
// thing the user is actually typing". Each alias's identity is what
// `normalizeDraftCommand` would resolve it to (reusing the SAME function
// `shellKeyboardCommandAliases` itself is built from, so the INSERT→BLOCK
// special case and the T/I local overrides stay consistent by
// construction) when that's non-null, or a descriptor-scoped sentinel
// (guaranteed to never equal any real `DraftCommandId`) for a non-draft
// descriptor's own aliases. A draft key is ambiguous iff some longer
// alias ANYWHERE in this universe shares its prefix and carries a
// DIFFERENT identity — a strict superset of the old draft-only check
// (every previously-ambiguous key stays ambiguous; the universe only
// grows), so this is a monotonic broadening, not a behaviour swap.
function buildAliasIdentityUniverse(): ReadonlyMap<string, string> {
  const universe = new Map<string, string>();
  for (const descriptor of ALL_COMMAND_DESCRIPTORS) {
    const candidates = [
      descriptor.id,
      ...descriptor.aliases.en,
      ...descriptor.aliases.ru,
    ];
    for (const candidate of candidates) {
      if (universe.has(candidate)) continue;
      const draftIdentity = normalizeDraftCommand(candidate);
      universe.set(candidate, draftIdentity ?? `__nondraft__:${descriptor.id}`);
    }
  }
  return universe;
}

const aliasIdentityUniverse: ReadonlyMap<string, string> =
  buildAliasIdentityUniverse();

function computeAmbiguousShellKeyboardAliases(
  table: Readonly<Record<string, DraftCommandId>>,
  universe: ReadonlyMap<string, string>,
): ReadonlySet<string> {
  const universeKeys = [...universe.keys()];
  const ambiguous = new Set<string>();
  for (const key of Object.keys(table)) {
    const resolved = table[key];
    for (const other of universeKeys) {
      if (other.length <= key.length || !other.startsWith(key)) continue;
      if (universe.get(other) !== resolved) {
        ambiguous.add(key);
        break;
      }
    }
  }
  return ambiguous;
}

const ambiguousShellKeyboardAliases: ReadonlySet<string> =
  computeAmbiguousShellKeyboardAliases(
    shellKeyboardCommandAliases,
    aliasIdentityUniverse,
  );

// D5 — `buildCommandSuggestions`'s no-session autocomplete list was the
// THIRD divergent alias source the audit found: another hand-written
// `[alias, command] as const` array, drifted from both the registry and
// `shellKeyboardCommandAliases` above (e.g. it was missing WIPEOUT / SPLINE
// / BEDIT / BSAVE / BCLOSE entirely). Derived from the SAME registry pass:
// every EN alias of every drafting-capable descriptor pairs with the
// descriptor's own canonical `id` (NOT its `draftCommandId` — INSERT must
// still suggest "INSERT (I)", matching what the user actually typed, even
// though it drafts through the BLOCK flow internally).
//
// D5 follow-up (@codex round-2, Finding 4) — filtering on `draftCommandId
// !== undefined` alone silently dropped EVERY non-draft command from the
// list, regressing ERASE (select-then-act, no draft state machine, no
// `draftCommandId`) out of autocomplete even though the old hand-written
// array explicitly carried "E"/ERASE and `submitCommandText` still
// dispatches it via the registry. The correct signal for "a real,
// currently-working, user-facing command line entry" is NOT draft-flow
// participation — it's `uiMetadata.ribbonTab !== null` (the SAME field the
// Ribbon UI already uses to distinguish user-facing commands from
// internal/AI-only system commands — see `RibbonTab`'s doc comment in
// command-registry's types.ts: "null for descriptors that should not
// appear on the ribbon") combined with `status === "implemented"`
// (excludes stub placeholders like MSPACE/PSPACE that don't dispatch
// anything yet). This adds ERASE plus the small handful of other
// non-draft, ribbon-visible, implemented commands (ATTSYNC, EXPLODEINSERT,
// LAYOUT) without reintroducing a hand-picked id allowlist — every other
// non-draft descriptor (the ~60 internal/system/attribute/dynamic/xref
// commands with `ribbonTab: null`) stays excluded, matching the old list's
// scope.
//
// D5 round-3 (@codex Finding 1) — `status === "implemented" && ribbonTab
// !== null` is NECESSARY but not SUFFICIENT for "typing this at the
// command line actually works": it's a signal about the REDUCER/UI side
// (the descriptor has a real reducer + belongs on the ribbon), not about
// whether `submitCommandText`'s dispatch chain has a route for it.
// EXPLODEINSERT satisfies both yet has NO route anywhere — no
// `draftCommandId` (so `resolveShellDraftCommand` never fires), no
// `resolvedCommandId === "EXPLODEINSERT"` branch in ANY commandDispatch/*
// handler module (unlike ATTSYNC → systemCommands.ts, LAYOUT RENAME →
// systemCommands.ts, ERASE → submitCommandText.ts's own prelude — see the
// registry's own block.ts comment: "AI surface deferred to CC-2c"), AND no
// case for it anywhere in `reduceEditorShellState`'s tail `switch` either
// (see the round-4 note below on where LAYOUT's OTHER sub-verbs land). The
// suggestion dropdown advertised it, but pressing Enter fell all the way
// through to the generic `commandline/submit` catch-all and produced
// `audit.command_not_wired` — a real, user-visible dead end. Excluded here
// as a deliberately tiny, explicitly-tracked gap (NOT a silent allowlist
// hack): `useCommandLine.suggestions.notWiredGap.test.ts` asserts the
// suggestion list drops it while `commandDispatch/submitCommandText.notWiredGap.test.ts`
// drives the REAL (no sibling-module mocking) dispatch chain for all four
// current members of the visible-non-draft set — proving ERASE / ATTSYNC /
// LAYOUT RENAME each reach a real dispatch/dialog call while
// EXPLODEINSERT/XPLODE reaches ONLY the generic fallback. Either test fails
// loudly the day EXPLODEINSERT gains a real route (the signal to delete
// this entry).
//
// D5 round-4 (@codex Finding 1) — LAYOUT stays OUT of this exclusion set
// even though the suggestion dropdown offers the BARE "LAYOUT" alias (this
// list pairs every alias with the descriptor's bare `id`, not a
// subcommand). Bare "LAYOUT" — and its SET / NEW / DELETE / raw-space-id
// forms — have no `commandDispatch/systemCommands.ts` branch the way
// LAYOUT RENAME does, so at the `submitCommandText` router layer alone
// they fall through to `commandline/submit` exactly like EXPLODEINSERT
// does. The difference is what happens NEXT: `commandline/submit` is
// itself a real, further-dispatched action — `reduceEditorShellState`'s
// tail `switch` routes it to `editorStore.ts`'s `routeLayoutCommand`,
// which HAS a `normalizedText === "LAYOUT"` branch (Phase MC1 — lists the
// available spaces) plus SET/NEW/DELETE branches, all reducer-tested
// end-to-end (`editorStore.test.ts`'s "LAYOUT bare command reaches real
// handling, NOT the generic audit.command_not_wired fallback" +
// `editorStore.layoutSet/New/Delete.test.ts`). EXPLODEINSERT has no such
// downstream case ANYWHERE, which is exactly what keeps IT excluded.
// `commandDispatch/submitCommandText.notWiredGap.test.ts` now asserts the
// bare-LAYOUT-reaches-`commandline/submit` half of this at the router
// layer; the reducer-layer half lives in `editorStore.test.ts` as noted
// above. Pressing Enter on the "LAYOUT" suggestion is a real command
// (AutoCAD's `LAYOUT` `?`-equivalent listing), not a no-op or a
// misrouted subcommand.
export const NOT_YET_CLI_DISPATCHED_NON_DRAFT_IDS: ReadonlySet<string> =
  new Set(["EXPLODEINSERT"]);

function buildCommandSuggestionAliasPairs(): Array<[string, string]> {
  const pairs: Array<[string, string]> = [];
  const seen = new Set<string>();
  for (const descriptor of ALL_COMMAND_DESCRIPTORS) {
    // Round-5 fix — a `draftCommandId` alone is NOT sufficient for "safe
    // to advertise as a command-line suggestion": palette-only draft
    // commands (`PALETTE_ONLY_DRAFT_COMMAND_IDS`, e.g. GCCONSTRAINT) can
    // only start correctly with options a specific UI layer supplies, and
    // get permanently stuck if `submitCommandText` starts them directly
    // from a suggestion click or typed Enter. Exclude them here the same
    // way `NOT_YET_CLI_DISPATCHED_NON_DRAFT_IDS` excludes non-draft
    // commands with no working dispatch route.
    const isDraft =
      descriptor.draftCommandId !== undefined &&
      !PALETTE_ONLY_DRAFT_COMMAND_IDS.has(
        descriptor.draftCommandId as DraftCommandId,
      );
    const isVisibleNonDraft =
      descriptor.status === "implemented" &&
      descriptor.uiMetadata.ribbonTab !== null &&
      !NOT_YET_CLI_DISPATCHED_NON_DRAFT_IDS.has(descriptor.id);
    if (!isDraft && !isVisibleNonDraft) continue;
    for (const alias of descriptor.aliases.en) {
      const key = `${alias} ${descriptor.id}`;
      if (seen.has(key)) continue;
      seen.add(key);
      pairs.push([alias, descriptor.id]);
    }
  }

  // D5 follow-up (@codex round-2, Finding 3) — a registry alias one
  // descriptor legitimately owns (MTEXT really does claim AutoCAD's
  // documented "T" alias — see annotation.ts) can collide with a LOCAL
  // shell-keyboard override on the SAME key that resolves to a DIFFERENT
  // command (T→TEXT; TEXT cannot claim "T" itself without a registry
  // C.ALIAS collision — see `SHELL_KEYBOARD_ALIAS_LOCAL_OVERRIDES`'s own
  // doc comment above). Left unreconciled, the suggestion dropdown for
  // bare "T" advertises "MTEXT (T)" — a lie: pressing Enter on the CURRENT
  // buffer "T" actually starts TEXT (`resolveShellDraftCommand` consults
  // the override table first). Drop any pair whose alias is an override
  // key but whose resolved Enter-outcome disagrees with the override
  // (generic — keyed off `SHELL_KEYBOARD_ALIAS_LOCAL_OVERRIDES` itself, not
  // a hard-coded "T"), then backfill a pair for an override key ONLY if no
  // surviving pair already represents its target command under any OTHER
  // alias. I→BLOCK needs no backfill (nothing is even dropped for it —
  // INSERT's OWN registry alias "I" already resolves to BLOCK, so
  // "INSERT (I)" already matches Enter's behaviour); T→TEXT needs no
  // backfill either once "MTEXT (T)" is dropped, because "TEXT (TEXT)" /
  // "TEXT (DT)" / "TEXT (DTEXT)" already correctly represent TEXT.
  const reconciled = pairs.filter(([alias, command]) => {
    if (
      !Object.prototype.hasOwnProperty.call(
        SHELL_KEYBOARD_ALIAS_LOCAL_OVERRIDES,
        alias,
      )
    ) {
      return true;
    }
    return resolveShellDraftCommand(alias) === normalizeDraftCommand(command);
  });
  for (const [overrideAlias, overrideTarget] of Object.entries(
    SHELL_KEYBOARD_ALIAS_LOCAL_OVERRIDES,
  )) {
    const alreadyRepresented = reconciled.some(
      ([, command]) => normalizeDraftCommand(command) === overrideTarget,
    );
    if (alreadyRepresented) continue;
    const targetDescriptor = ALL_COMMAND_DESCRIPTORS.find(
      (descriptor) => descriptor.id === overrideTarget,
    );
    if (targetDescriptor) {
      reconciled.push([overrideAlias, targetDescriptor.id]);
    }
  }
  return reconciled;
}

const COMMAND_SUGGESTION_ALIAS_PAIRS: Array<[string, string]> =
  buildCommandSuggestionAliasPairs();

export function resolveShellDraftCommand(
  rawText: string,
): DraftCommandId | null {
  const normalized = rawText.trim().toUpperCase();
  const resolved =
    shellKeyboardCommandAliases[normalized] ?? normalizeDraftCommand(normalized);
  // Belt-and-suspenders gate: `shellKeyboardCommandAliases` already omits
  // palette-only draft commands (see `PALETTE_ONLY_DRAFT_COMMAND_IDS`
  // above), but `normalizeDraftCommand`'s registry fallback has no
  // knowledge of this CLI-specific exclusion and WOULD still resolve the
  // full alias (e.g. typing "GCCONSTRAINT" or "ГЕОМОГРАН" in full). Gating
  // the final resolved value here — not just the table — means every
  // caller of this function is covered by one choke point: the
  // Enter-submission path in `submitCommandText.ts` AND the no-session
  // instant-keystroke auto-submit path via `isExactShellKeyboardAlias`.
  if (resolved !== null && PALETTE_ONLY_DRAFT_COMMAND_IDS.has(resolved)) {
    return null;
  }
  return resolved;
}

// D5 follow-up (@codex round-2, Finding 1) — `isExactShellKeyboardAlias`'s
// ambiguity filter below is scoped to ONE specific consumer: the
// no-Enter-needed canvas keyboard auto-submit path (`useKeyboardShortcuts`,
// itself only reached when NO draft session is active — see the `if
// (draftSession) return;` guard right before its call site). It must NOT be
// reused for "does this text look like a known command-line token at all" —
// that broader presence-only question is answered by
// `isKnownShellKeyboardAlias` below, and is what the ACTIVE-DRAFT-SESSION
// short-alias guard (FIX-6 in submitCommandText.ts) needs. Conflating the
// two regressed that guard: once "L" became ambiguous (LINE vs LEN /
// LWPOLYLINE) it stopped satisfying `isExactShellKeyboardAlias`, so a bare
// "L" typed at MOVE's `move-base-point` prompt fell through the guard into
// `resolveShellDraftCommand("L")` = "LINE", silently destroying the active
// MOVE session via `beginDraftingWithColorReset("LINE")`. Two functions,
// two questions:
//   • `isKnownShellKeyboardAlias` — "is this EXACTLY a table key?" (no
//     ambiguity filter). Use for guards that just need to recognize
//     command-line tokens (active-session short-alias guard).
//   • `isExactShellKeyboardAlias` — "is this a table key AND safe to
//     auto-submit without Enter right now?" (ambiguity filter applied).
//     Use ONLY for the no-session instant-keystroke auto-submit path.
export function isKnownShellKeyboardAlias(rawText: string): boolean {
  const normalized = rawText.trim().toUpperCase();
  return Object.prototype.hasOwnProperty.call(
    shellKeyboardCommandAliases,
    normalized,
  );
}

export function isExactShellKeyboardAlias(rawText: string): boolean {
  const normalized = rawText.trim().toUpperCase();
  return (
    isKnownShellKeyboardAlias(normalized) &&
    !ambiguousShellKeyboardAliases.has(normalized)
  );
}

export function isShellKeyboardCharacter(key: string): boolean {
  return /^[A-Za-z ]$/u.test(key);
}

export function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  if (target.isContentEditable) {
    return true;
  }

  return ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName);
}

export function buildCommandSuggestions(
  rawText: string,
  session: DraftSession | null,
): CommandSuggestion[] {
  const text = rawText.trim();
  const normalized = text.toUpperCase();
  const suggestions: CommandSuggestion[] = [];

  if (!session) {
    if (!text) {
      return [];
    }

    const matchingPairs = COMMAND_SUGGESTION_ALIAS_PAIRS.filter(
      ([alias, command]) =>
        alias.startsWith(normalized) || command.startsWith(normalized),
    );

    // D5 follow-up (@codex round-2, Finding 2) — an EXACT alias match must
    // survive the `.slice(0, 6)` cutoff at the bottom of this function even
    // when other prefix matches would otherwise fill every visible slot
    // first in registry-iteration order. Concrete case: with round-1's
    // ambiguity guard now withholding auto-submit for bare "M" (pending
    // Enter), the suggestion dropdown becomes the primary visible signal —
    // but MTEXT/MLINE/MLEADER's M*-prefixed aliases sort before MOVE's bare
    // "M" in `ALL_COMMAND_DESCRIPTORS` order, so MOVE fell off the end of
    // the 6-item list even though Enter would run it. Stable-sort exact
    // matches to the front so the command Enter actually runs is always
    // shown.
    matchingPairs.sort((a, b) => {
      const aExact = a[0] === normalized ? 0 : 1;
      const bExact = b[0] === normalized ? 0 : 1;
      return aExact - bExact;
    });

    for (const [alias, command] of matchingPairs) {
      suggestions.push({
        id: `command-${alias}-${command}`,
        label: `${command} (${alias})`,
        insertText: command,
        submit: true,
        kind: "command",
      });
    }
  } else {
    for (const keyword of buildDraftKeywords(session)) {
      if (keyword.toUpperCase().startsWith(normalized)) {
        suggestions.push({
          id: `keyword-${keyword}`,
          label: localizeDraftKeyword(keyword),
          insertText: keyword,
          submit: true,
          kind: "keyword",
        });
      }
    }

    for (const example of ["1200,3500", "@4500,0", "@500<45", "500"]) {
      if (!text || /^[@\d\-.]/u.test(text)) {
        if (!text || example.startsWith(text)) {
          suggestions.push({
            id: `example-${example}`,
            label: t("suggestion.coordinate_input"),
            insertText: example,
            kind: "example",
          });
        }
      }
    }
  }

  return suggestions.slice(0, 6);
}

export function buildDraftKeywords(session: DraftSession): string[] {
  switch (session.step) {
    case "line-next-point":
      return session.points.length >= 2 ? ["Close"] : [];
    case "circle-radius":
      return ["Diameter"];
    case "polyline-next-point":
      return session.points.length >= 2 ? ["Close", "Undo"] : ["Undo"];
    // REVCLOUD-MODES — the AutoCAD first-prompt options + sub-prompt
    // keyword sets (Modify is intentionally absent until the
    // revision-cloud properties editor lands).
    case "revcloud-first-corner":
      return [
        "Arc length",
        "Object",
        "Rectangular",
        "Polygonal",
        "Freehand",
        "Style",
      ];
    case "revcloud-style":
      return ["Normal", "Calligraphy"];
    case "revcloud-object-reverse":
      return ["Yes", "No"];
    default:
      return [];
  }
}

// Maps the internal English draft keyword (which is also the accepted
// text input for the command engine) to its translated display label.
function localizeDraftKeyword(keyword: string): string {
  switch (keyword) {
    case "Close":
      return t("keyword.close");
    case "Diameter":
      return t("keyword.diameter");
    case "Undo":
      return t("keyword.undo");
    // REVCLOUD-MODES keyword labels.
    case "Arc length":
      return t("keyword.revcloud.arc_length");
    case "Object":
      return t("keyword.revcloud.object");
    case "Rectangular":
      return t("keyword.revcloud.rectangular");
    case "Polygonal":
      return t("keyword.revcloud.polygonal");
    case "Freehand":
      return t("keyword.revcloud.freehand");
    case "Style":
      return t("keyword.revcloud.style");
    case "Normal":
      return t("keyword.revcloud.style_normal");
    case "Calligraphy":
      return t("keyword.revcloud.style_calligraphy");
    case "Yes":
      return t("keyword.yes");
    case "No":
      return t("keyword.no");
    default:
      return keyword;
  }
}

// A03 (R17 seam: APP-COMMAND-ENTRY-SEAM) — the inline `command-input-inline`
// element's OWN local Enter/Escape handling, moved verbatim out of App.tsx's
// JSX `onKeyDown` prop (behaviour-preserving; no branch, guard, or ordering
// changed). This is DISTINCT from the GLOBAL `window` keydown listener in
// `useKeyboardShortcuts.ts`: it only runs while THIS specific focused input
// element receives the event, and several branches below exist specifically
// because they must run — and `preventDefault()` — BEFORE that global
// listener does (see the inline comments for each case).
//
// Deps type inlines the exact same field shapes `useKeyboardShortcuts.ts`'s
// `KeyboardShortcutDeps` already uses for `submitCommandTextRef`'s pointee,
// `setDraftSession`, `setCommandText`, `setCommandHistoryOpen` and
// `clearColorTransientState` — App.tsx passes the SAME values to both, so
// the shapes must stay identical. `submitCommandText` here is a plain
// function value (App.tsx calls it directly in this JSX branch, not via a
// ref), typed with `SubmitCommandTextFn`'s exact structural shape rather
// than importing that type from `commandDispatch/submitCommandText.ts` —
// that module already imports FROM this file, so importing its type back
// here would create an import cycle.
export type CommandLineInputKeyDownDeps = {
  panSessionActiveRef: MutableRefObject<boolean>;
  commandText: string;
  endPanSession: (options?: { silent?: boolean }) => void;
  submitCommandText: (rawText: string, source?: "user" | "batch") => boolean;
  draftSession: DraftSession | null;
  setDraftSession: (session: DraftSession | null) => void;
  setCommandText: (text: string | ((prev: string) => string)) => void;
  setCommandHistoryOpen: (open: boolean | ((prev: boolean) => boolean)) => void;
  clearColorTransientState: () => void;
};

// Factory pattern (mirrors `createSubmitCommandText` in
// `commandDispatch/submitCommandText.ts`) — App.tsx calls this fresh on
// every render, exactly matching the lifetime of the inline JSX arrow
// function it replaces (the closures below must see the CURRENT render's
// `commandText` / `draftSession` values, not stale ones).
export function createCommandLineInputKeyDownHandler(
  deps: CommandLineInputKeyDownDeps,
): (event: ReactKeyboardEvent<HTMLInputElement>) => void {
  const {
    panSessionActiveRef,
    commandText,
    endPanSession,
    submitCommandText,
    draftSession,
    setDraftSession,
    setCommandText,
    setCommandHistoryOpen,
    clearColorTransientState,
  } = deps;

  return function handleCommandLineInputKeyDown(event) {
    if (event.key === "Enter") {
      event.preventDefault();
      // cmd-pan r5 P2-2 — PAN can be STARTED from the command line
      // (bare PAN/ПАН/P), and focus then stays in THIS input (the
      // r4 focus-guard makes the capture-phase PAN-exit handler BAIL
      // for `command-input-inline`). The prompt promises "Esc/Enter
      // exits", but an EMPTY Enter bails inside `submitCommandText`
      // at its `!trimmedText && !draftSession` guard BEFORE reaching
      // the pan teardown — so without this the session would stick
      // until focus returned to the canvas. An empty Enter while a
      // pan is active therefore ENDS the pan here (don't submit an
      // empty command). A NON-empty Enter still falls through to
      // `submitCommandText`, which supersedes the pan at its
      // command-start choke point (already works).
      if (panSessionActiveRef.current && commandText.trim() === "") {
        endPanSession();
        return;
      }
      void submitCommandText(commandText);
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      // Point-filter / temporary-tracking (FROM/M2P/.X.Y.Z):
      // this handler preventDefault()s before the global
      // window Escape listener (useKeyboardShortcuts.ts) runs
      // — that listener bails immediately on
      // `event.defaultPrevented`, so its own filter-cancel
      // branch never fires for a command-input-focused Esc.
      // Mirror it HERE: cancel just the sub-prompt, keep the
      // host command (AutoCAD parity), skip the full
      // draft/text/colour teardown below. (Codex PR #504 P2 —
      // "Route command-input Escape through the active
      // filter".)
      //
      // Still clear the command-line TEXT buffer, though: an
      // unsubmitted continuation typed before this Escape
      // (e.g. a partial `@10,0` offset) must not survive into
      // the surviving host prompt — without this the next
      // Enter would resubmit the stale text to the now-bare
      // LINE/MOVE point prompt. Only the draft/history/colour
      // teardown below is skipped (those belong to a FULL
      // command abort, not a filter-only cancel). (Codex PR
      // #504 P3 — "Clear input text when cancelling a
      // filter".)
      if (draftSession && isPointFilterActive(draftSession)) {
        // local Codex round-10 P2 — mirrors the SAME fix in
        // `useKeyboardShortcuts.ts`'s global Escape handler:
        // clearing just the point filter here returns to the
        // SAME host command/step in place (command unchanged,
        // step unchanged, no point committed), so none of the
        // App lifecycle effect's clear triggers fire for a
        // concurrently-armed/captured Extended Intersection
        // override. Clear it HERE too, before the filter
        // cancel, so it cannot survive as a stale operand pick
        // that swallows the next canvas click.
        if (useSysvarStore.getState().osnapExtendedOverride != null) {
          useSysvarStore.getState().setOsnapExtendedOverride(null);
        }
        setDraftSession(clearPointFilter(draftSession));
        setCommandText("");
        return;
      }
      // W2.5 OSNAP-OVERRIDE-MODES-S2 (local Codex round-7
      // follow-up, found while building this slice's own E2E
      // regression coverage for the round-7 fix) — the SAME
      // command-input-focused-Escape gap the point-filter
      // branch above already documents applies to the Extended
      // Intersection override too: the command line stays
      // focused (id="command-input-inline") through the whole
      // arm/capture/hover sequence (nothing in that flow blurs
      // it), so `useKeyboardShortcuts.ts`'s global Escape
      // handler's own override-clear branch never runs for
      // this Escape (targetIsEditable && !isCommandInputTarget
      // bails first, same id mismatch as the point-filter
      // case). Without this mirror, Escape while the command
      // line has focus would silently leave the override
      // ARMED/CAPTURED forever — never cleared by any seam —
      // instead of cancelling just the override sub-prompt
      // (AutoCAD parity, keeps the host command alive).
      if (useSysvarStore.getState().osnapExtendedOverride != null) {
        useSysvarStore.getState().setOsnapExtendedOverride(null);
        return;
      }
      // cmd-pan r5 P2-2 — Escape from the FOCUSED command line must
      // also end an active realtime-pan session (the prompt's
      // "Esc/Enter exits" contract). The capture-phase PAN-exit
      // handler bails for this input (r4 focus-guard), and this
      // handler's own Escape only cleared text/history below — so the
      // pan would otherwise persist. End it here IN ADDITION TO the
      // existing text/history/colour-context teardown.
      if (panSessionActiveRef.current) endPanSession();
      setDraftSession(null);
      setCommandText("");
      setCommandHistoryOpen(false);
      // CECOLOR round-9 ROOT CURE — this inline command-input
      // Escape is its OWN colour-context-end seam: the input id is
      // `command-input-inline` so the global Escape handler
      // early-returns (it whitelists `command-input`) AND this
      // path `preventDefault()`s before the window listener runs,
      // so the shared clearer in `useKeyboardShortcuts` never fires
      // here. Route through `clearColorTransientState()` so Escape
      // in the command input tears down the SAME state as every
      // other seam: closes the command-line COLOR picker, clears
      // the `-COLOR` pending-reply flag, and bumps the swatch reset
      // epoch (closing the ribbon swatch's local picker).
      clearColorTransientState();
    }
  };
}
